import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';

// Material UI
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import Alert from '@material-ui/lab/Alert';



// Data
import { useCompanySportTerminology } from '../../../lib/stores/company.store';
import { CommunicationContextType, SportTerminology } from '../../../lib/types';
import { usePublishCommunicationContext } from '../../../lib/stores/communication.store';
import { pluralTerm } from '../../../lib/helpers/term.helper';

// Components
import DashboardLayout from '../_layout/DashboardLayout/dashboard.layout';
import { PageTitle, SectionHeading } from '../../_core/_ui/typography.component';
import { SectionWrapper, Spacer } from '../../_core/_ui/structure.components';
import { EditAssessmentSessionGraphQL } from '../../Session/EditAssessmentSession/edit-assessment-session.graphql';
import AssessmentSessionDrillList
  from '../../Session/AssessmentSessionDrillList/assessment-session-drill-list.component';
import AssessmentSessionPracticePlanList
  from '../../Session/AssessmentSessionPracticePlanList/assessment-session-practice-plan-list.component';
import { AssessmentSessionPlayerAssessmentList }
  from '../../Session/AssessmentSessionPlayerList/assessment-session-player-list.component';
import { SecondaryButton } from '../../_core/_ui/buttons.component';
import AssessmentSessionUpcomingNotificationsGraphQL
  from '../../Session/AssessmentSessionUpcomingNotifications/assessment-session-upcoming-notifications.graphql';
import { AssessmentSessionCompletedNotificationsGraphQL } from '../../Session/AssessmentSessionCompletedNotifications/assessment-session-completed-notifications.graphql';
import { AssessmentSessionEvaluationHistoryGraphQL } from '../../Session/AssessmentSessionEvaluationHistory/assessment-session-evaluation-history.graphql';
import { hasAnyRoles } from '../../../lib/services/user.service';
import { ROLES } from '../../../../_configuration';
import { gql, useQuery } from '@apollo/client';
import { CircularProgress } from '@material-ui/core';

interface IEditAssessmentSessionPage {
  match: any;
}

const GET_ASSESSMENT_SESSION_AGE_GROUP = gql`
  query GetAssessmentSessionAgeGroup($_id: ID!) {
    assessmentSession(_id: $_id) {
      _id
      ageGroupId
      unappliedActionsCount
    }
  }
`;

const EditAssessmentSessionPage: React.FC<IEditAssessmentSessionPage> = ({ match }) => {
  const termLookup = useCompanySportTerminology();
  const history = useHistory();
  const { assessmentSessionId } = match.params;

  // Publish this assessment session as the compose panel's page context; it opens
  // pre-applied as a locked row, and a cross-assessment id drops out on resolve.
  usePublishCommunicationContext(useMemo(
    () => [{ type: CommunicationContextType.ASSESSMENT_SESSION, id: assessmentSessionId }],
    [assessmentSessionId]
  ));

  const { data, loading } = useQuery(GET_ASSESSMENT_SESSION_AGE_GROUP, {fetchPolicy: "cache-and-network", variables: {_id: assessmentSessionId}})
  const hasReportingRole = hasAnyRoles({ageGroupId: data?.assessmentSession?.ageGroupId, roles: [ROLES.REPORTS]});

  return (
    <DashboardLayout requiredRoles={[ROLES.CONFIGURATION]}>
      <Box display="flex" mb={2}>
        <PageTitle>Edit {termLookup[SportTerminology.SESSION]}</PageTitle>
        <Spacer />
        <SecondaryButton
          startIcon={<ArrowBackIosIcon />}
          onClick={() => history.push('/sessions')}
        >
          Back
        </SecondaryButton>
      </Box>
      <Grid container spacing={2}>
        {!!data?.assessmentSession?.unappliedActionsCount && (
          <Grid item xs={12}>
            <Alert elevation={0} severity="warning" variant="filled">Found {data?.assessmentSession?.unappliedActionsCount} new scores</Alert>
          </Grid>
        )}
        <Grid item xs={12}>
          <SectionWrapper>
            <EditAssessmentSessionGraphQL assessmentSessionId={assessmentSessionId} />
          </SectionWrapper>
        </Grid>
        <Grid item xs={12} md={6}>
          <SectionWrapper>
            <SectionHeading>{pluralTerm(termLookup[SportTerminology.ATHLETE])}</SectionHeading>
            <AssessmentSessionPlayerAssessmentList assessmentSessionId={assessmentSessionId} />
          </SectionWrapper>
        </Grid>
        <Grid item xs={12} md={6}>
          <SectionWrapper>
            <SectionHeading>Upcoming Notifications</SectionHeading>
            <AssessmentSessionUpcomingNotificationsGraphQL assessmentSessionId={assessmentSessionId} />
          </SectionWrapper>
        </Grid>
        <Grid item xs={12} md={6}>
          <SectionWrapper>
            <SectionHeading>Practice Plans</SectionHeading>
            <AssessmentSessionPracticePlanList assessmentSessionId={assessmentSessionId} />

            <SectionHeading>{pluralTerm(termLookup[SportTerminology.EVENT])}</SectionHeading>
            <AssessmentSessionDrillList assessmentSessionId={assessmentSessionId} />
          </SectionWrapper>
        </Grid>
        <Grid item xs={12} md={6}>
          <SectionWrapper>
            <SectionHeading>Completed Notifications</SectionHeading>
            <AssessmentSessionCompletedNotificationsGraphQL assessmentSessionId={assessmentSessionId} />
          </SectionWrapper>
        </Grid>
        {loading ?
          <Grid item xs={12}>
            <CircularProgress />
          </Grid> :
          !!hasReportingRole ? (
            <Grid item xs={12}>
              <SectionWrapper>
                <SectionHeading>Evaluation History</SectionHeading>
                <AssessmentSessionEvaluationHistoryGraphQL assessmentSessionId={assessmentSessionId} />
              </SectionWrapper>
            </Grid>
          ) : null
        }
      </Grid>
    </DashboardLayout>
  );
};

export default EditAssessmentSessionPage;
